import React, { useMemo, useEffect, useState, useRef } from 'react';
import { FinancialTransaction } from '../../services/database/supabase';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';

interface TransactionListProps {
  transactions: FinancialTransaction[];
  isBlurred: boolean;
  viewMode: 'total' | 'month';
  selectedDate: string;
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  isBlurred,
  viewMode,
  selectedDate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  // Verificar transações pendentes
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pendingTransactions = transactions.filter(transaction => {
      const dueDate = new Date(transaction.date);
      dueDate.setHours(0, 0, 0, 0);
      
      return (
        transaction.status === 'Em Andamento' &&
        dueDate.getTime() === today.getTime()
      );
    });

    // Notificar sobre transações pendentes
    pendingTransactions.forEach(transaction => {
      toast.warning(`Transação pendente para hoje: ${transaction.description}`, {
        description: `Valor: R$ ${transaction.amount.toFixed(2)}`,
        duration: 5000,
      });
    });
  }, [transactions]);

  // Filtrar e ordenar transações
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filtrar por mês se necessário
    if (viewMode === 'month') {
      const [year, month] = selectedDate.split('-').map(Number);
      filtered = filtered.filter(transaction => {
        const date = new Date(transaction.date);
        return date.getFullYear() === year && date.getMonth() === month - 1;
      });
    }

    // Ordenar por data (mais recente primeiro)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  }, [transactions, viewMode, selectedDate]);

  // Mostrar apenas as 3 transações mais recentes quando minimizado
  const displayedTransactions = isExpanded 
    ? filteredTransactions 
    : filteredTransactions.slice(0, 3);

  // Função para gerar análise mensal
  const generateMonthlyAnalysis = (transactions: FinancialTransaction[], totalReceitas: number, totalDespesas: number, saldo: number) => {
    // Categorizar transações
    const categorias = transactions.reduce((acc, t) => {
      if (!acc[t.category]) {
        acc[t.category] = { total: 0, count: 0 };
      }
      acc[t.category].total += t.amount;
      acc[t.category].count += 1;
      return acc;
    }, {} as Record<string, { total: number, count: number }>);

    // Encontrar maior despesa
    const maiorDespesa = transactions
      .filter(t => t.type === 'Despesas' || t.type === 'Carro')
      .reduce((max, t) => t.amount > max.amount ? t : max, { amount: 0 } as FinancialTransaction);

    // Calcular percentual de economia
    const percentualEconomia = totalReceitas > 0 ? ((totalReceitas - totalDespesas) / totalReceitas) * 100 : 0;

    // Categoria com mais gastos
    const categoriaMaisGastos = Object.entries(categorias)
      .filter(([cat]) => cat !== 'Salário' && cat !== 'Receita')
      .reduce((max, [cat, data]) => data.total > max.total ? { categoria: cat, ...data } : max, 
        { categoria: '', total: 0, count: 0 });

    // Gerar texto de análise
    let analise = '';
    
    if (saldo >= 0) {
      analise = `Parabéns! Você teve um mês positivo com saldo de ${new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(saldo)}. `;
    } else {
      analise = `Atenção! Você teve um mês negativo com saldo de ${new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(saldo)}. `;
    }

    analise += `Sua maior despesa foi "${maiorDespesa.description}" no valor de ${new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(maiorDespesa.amount)}. `;

    if (categoriaMaisGastos.categoria) {
      analise += `A categoria "${categoriaMaisGastos.categoria}" representou seus maiores gastos, `;
      analise += `com ${categoriaMaisGastos.count} transações totalizando ${new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(categoriaMaisGastos.total)}. `;
    }

    if (percentualEconomia > 0) {
      analise += `Você conseguiu economizar ${percentualEconomia.toFixed(1)}% da sua receita total.`;
    } else {
      analise += `Suas despesas superaram suas receitas neste mês.`;
    }

    return analise;
  };

  // Função para baixar PDF
  const downloadPDF = async () => {
    try {
      toast.info('Gerando PDF...');

      const [year, month] = selectedDate.split('-');
      const monthName = new Date(Number(year), Number(month) - 1).toLocaleString('pt-BR', { month: 'long' });

      // Importar dependências
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      // Calcular totais primeiro para usar em todo o relatório
      const totalReceitas = filteredTransactions
        .filter(t => t.type === 'Receita' || t.type === 'Salário')
        .reduce((sum, t) => sum + t.amount, 0);

      const totalDespesas = filteredTransactions
        .filter(t => t.type === 'Despesas' || t.type === 'Carro')
        .reduce((sum, t) => sum + t.amount, 0);

      const saldo = totalReceitas - totalDespesas;

      // Criar um elemento temporário para o relatório completo
      const reportContainer = document.createElement('div');
      reportContainer.style.padding = '60px';
      reportContainer.style.background = '#0a0a0a';
      reportContainer.style.color = 'white';
      reportContainer.style.fontFamily = 'Inter, system-ui, sans-serif';
      reportContainer.style.width = '1200px';
      reportContainer.style.margin = '0 auto';
      reportContainer.style.boxSizing = 'border-box';

      // Logo
      const logoContainer = document.createElement('div');
      logoContainer.style.textAlign = 'center';
      logoContainer.style.marginBottom = '40px';
      logoContainer.innerHTML = `
        <div style="background: linear-gradient(45deg, #00E4FF, #3B7EFF); padding: 20px; border-radius: 16px; display: inline-block;">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      `;
      reportContainer.appendChild(logoContainer);

      // Título
      const title = document.createElement('h1');
      title.style.textAlign = 'center';
      title.style.marginBottom = '40px';
      title.style.fontSize = '32px';
      title.style.fontWeight = '600';
      title.style.color = 'white';
      title.style.letterSpacing = '-0.025em';
      title.textContent = `Relatório de Transações - ${monthName} de ${year}`;
      reportContainer.appendChild(title);

      // Adicionar análise mensal
      const analysisContainer = document.createElement('div');
      analysisContainer.style.marginBottom = '40px';
      analysisContainer.style.background = 'rgba(255,255,255,0.02)';
      analysisContainer.style.borderRadius = '16px';
      analysisContainer.style.padding = '24px';
      analysisContainer.style.lineHeight = '1.6';
      analysisContainer.style.color = 'rgba(255,255,255,0.9)';
      analysisContainer.style.fontSize = '16px';
      analysisContainer.innerHTML = `
        <h2 style="margin-bottom: 16px; font-size: 24px; font-weight: 600; color: white; letter-spacing: -0.025em;">
          Análise Mensal
        </h2>
        <p style="margin: 0;">
          ${generateMonthlyAnalysis(filteredTransactions, totalReceitas, totalDespesas, saldo)}
        </p>
      `;
      reportContainer.appendChild(analysisContainer);

      // Container da tabela com scroll
      const tableContainer = document.createElement('div');
      tableContainer.style.marginBottom = '40px';
      tableContainer.style.background = 'rgba(255,255,255,0.02)';
      tableContainer.style.borderRadius = '16px';
      tableContainer.style.padding = '24px';

      // Tabela de transações
      const table = document.createElement('table');
      table.style.width = '100%';
      table.style.borderCollapse = 'separate';
      table.style.borderSpacing = '0 12px';

      // Cabeçalho da tabela
      const thead = document.createElement('thead');
      thead.innerHTML = `
        <tr>
          <th style="padding: 16px 20px; text-align: left; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Data</th>
          <th style="padding: 16px 20px; text-align: left; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Descrição</th>
          <th style="padding: 16px 20px; text-align: left; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Categoria</th>
          <th style="padding: 16px 20px; text-align: left; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Tipo</th>
          <th style="padding: 16px 20px; text-align: right; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Valor</th>
          <th style="padding: 16px 20px; text-align: right; color: rgba(255,255,255,0.5); font-weight: 500; font-size: 14px;">Status</th>
        </tr>
      `;
      table.appendChild(thead);

      // Corpo da tabela
      const tbody = document.createElement('tbody');
      filteredTransactions.forEach(t => {
        const tr = document.createElement('tr');
        tr.style.background = 'rgba(255,255,255,0.03)';
        tr.style.transition = 'background-color 0.2s';

        const isReceita = t.type === 'Receita' || t.type === 'Salário';
        const valorStyle = isReceita ? 'color: #10b981;' : 'color: #ef4444;';
        const amount = new Intl.NumberFormat('pt-BR', { 
          style: 'currency', 
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(t.amount);
        
        tr.innerHTML = `
          <td style="padding: 20px; border-radius: 12px 0 0 12px; color: white; font-size: 14px;">${(() => {
            const date = t.date;
            // Remove qualquer parte da string após o T
            const cleanDate = date.split('T')[0];
            const [year, month, day] = cleanDate.split('-');
            return `${day}/${month}/${year}`;
          })()}</td>
          <td style="padding: 20px; color: white; font-size: 14px; font-weight: 500;">${t.description}</td>
          <td style="padding: 20px; color: white; font-size: 14px;">${t.category}</td>
          <td style="padding: 20px; color: white; font-size: 14px;">${t.type}</td>
          <td style="padding: 20px; text-align: right; ${valorStyle} font-weight: 600; font-size: 14px;">${isBlurred ? '****' : amount}</td>
          <td style="padding: 20px; text-align: right; border-radius: 0 12px 12px 0; color: ${t.status === 'Em Andamento' ? '#eab308' : 'white'}; font-size: 14px;">${t.status}</td>
        `;
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      tableContainer.appendChild(table);
      reportContainer.appendChild(tableContainer);

      // Adicionar resumo
      const summary = document.createElement('div');
      summary.style.padding = '32px';
      summary.style.background = 'rgba(255,255,255,0.02)';
      summary.style.borderRadius = '16px';
      summary.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)';
      summary.innerHTML = `
        <h2 style="margin-bottom: 24px; font-size: 24px; font-weight: 600; color: white; letter-spacing: -0.025em;">Resumo</h2>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px;">
          <div>
            <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin-bottom: 8px;">Total de Receitas</p>
            <p style="color: #10b981; font-size: 24px; font-weight: 600;">${isBlurred ? '****' : formatValue(totalReceitas)}</p>
          </div>
          <div>
            <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin-bottom: 8px;">Total de Despesas</p>
            <p style="color: #ef4444; font-size: 24px; font-weight: 600;">${isBlurred ? '****' : formatValue(totalDespesas)}</p>
          </div>
          <div>
            <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin-bottom: 8px;">Saldo</p>
            <p style="color: ${saldo >= 0 ? '#10b981' : '#ef4444'}; font-size: 24px; font-weight: 600;">${isBlurred ? '****' : formatValue(saldo)}</p>
          </div>
        </div>
      `;
      reportContainer.appendChild(summary);

      // Adicionar o container ao documento temporariamente
      document.body.appendChild(reportContainer);

      try {
        // Converter para canvas com alta qualidade
        const canvas = await html2canvas(reportContainer, {
          backgroundColor: '#0a0a0a',
          scale: 3,
          useCORS: true,
          logging: false,
          allowTaint: true,
          letterRendering: true,
          onclone: (clonedDoc) => {
            const element = clonedDoc.querySelector('#pdf-container');
            if (element) {
              element.style.display = 'block';
            }
          }
        });

        // Criar o PDF com melhor qualidade
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [canvas.width / 2, canvas.height / 2],
          hotfixes: ['px_scaling'],
          compress: true
        });

        // Adicionar a imagem do canvas ao PDF com alta qualidade
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 1.0),
          'JPEG',
          0,
          0,
          canvas.width / 2,
          canvas.height / 2,
          undefined,
          'FAST'
        );

        // Salvar o PDF
        pdf.save(`transacoes_${monthName}_${year}.pdf`);
        toast.success('PDF gerado com sucesso!');
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        toast.error('Erro ao gerar PDF. Tente novamente.');
      } finally {
        // Remover o container temporário
        document.body.removeChild(reportContainer);
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF. Tente novamente.');
    }
  };

  // Função para formatar valores
  const formatValue = (value: number) => new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

  return (
    <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-6 border border-white/[0.08]">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Transações</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={downloadPDF}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            title="Baixar relatório em PDF"
          >
            <Download className="w-4 h-4" />
            <span>Baixar PDF</span>
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            {isExpanded ? (
              <>
                <span>Mostrar menos</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span>Mostrar mais</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
      <div className="space-y-4" ref={pdfRef}>
        {displayedTransactions.map((transaction) => (
          <div
            key={transaction.id}
            className="flex items-center justify-between p-4 bg-[#222]/50 rounded-xl border border-white/[0.08]"
          >
            <div className="flex-1">
              <p className="font-medium">{transaction.description}</p>
              <div className="flex items-center gap-2 text-sm text-white/60">
                {(() => {
                  const date = transaction.date;
                  // Remove qualquer parte da string após o T
                  const cleanDate = date.split('T')[0];
                  const [year, month, day] = cleanDate.split('-');
                  return <span>{`${day}/${month}/${year}`}</span>;
                })()}
                <span>•</span>
                <span>{transaction.category}</span>
                {transaction.status === 'Em Andamento' && (
                  <>
                    <span>•</span>
                    <span className="text-yellow-500">Pendente</span>
                  </>
                )}
              </div>
            </div>
            <div className={`text-right ${
              transaction.type === 'Receita' || transaction.type === 'Salário'
                ? 'text-emerald-500'
                : 'text-red-500'
            }`}>
              <p className="font-medium">
                {isBlurred ? '****' : `R$ ${transaction.amount.toFixed(2)}`}
              </p>
              <p className="text-sm opacity-60">{transaction.type}</p>
            </div>
          </div>
        ))}
        {!isExpanded && filteredTransactions.length > 3 && (
          <p className="text-center text-sm text-white/60 mt-4">
            {filteredTransactions.length - 3} transações ocultas
          </p>
        )}
      </div>
    </div>
  );
};
